import { Sync, SyncDisabled } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AbsenceDateRange } from '~/features/AbsenceChanges/EditAbsenceDialog/components/AbsenceFormFields/ShortAbsenceDurationFields/AbsenceDateRange';
import { AbsenceForm } from '~/features/AbsenceChanges/EditAbsenceDialog/data';
import { EditOverlappingAbsenceProps } from '~/features/AbsenceChanges/EditAbsenceDialog/useEditAbsenceDialogData';
import { PublicHolidayDto } from '~/services/Http/client.people.generated';
import { getDaysInRange } from '~/utils/helpers/date';

import { useSxStyles } from './useStyles';

export type ShortAbsenceDurationFieldsProps = {
  employeeId: number;
  publicHolidays: PublicHolidayDto[];
  onPickerMonthChange?: (month: Date) => void;
  overlappingAbsence: EditOverlappingAbsenceProps;
  isHoursFieldEnabled: (
    startDate?: Date | null,
    endDate?: Date | null,
  ) => boolean;
  daysFieldEditing: {
    canEditDays: boolean;
    onResetDaysField: () => void;
  };
};

type PropsType = {
  formMethods: UseFormReturn<AbsenceForm>;
} & ShortAbsenceDurationFieldsProps;

export const ShortAbsenceDurationFields = (props: PropsType) => {
  const {
    employeeId,
    formMethods,
    publicHolidays,
    onPickerMonthChange,
    overlappingAbsence,
    isHoursFieldEnabled,
    daysFieldEditing: { canEditDays, onResetDaysField },
  } = props;
  const {
    control,
    setValue,
    formState: { dirtyFields },
  } = formMethods;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const [startDate, endDate, days, hours] = useWatch({
    name: ['startDate', 'endDate', 'days', 'hours'],
    control: control,
  });
  const { isHoursEnabled, maxDays } = useMemo(() => {
    return {
      maxDays:
        !!startDate && !!endDate ? getDaysInRange(startDate, endDate, true) : 0,
      isHoursEnabled: isHoursFieldEnabled(startDate, endDate),
    };
  }, [isHoursFieldEnabled, startDate, endDate]);

  const daysFieldEdited: boolean = dirtyFields?.days ?? false;

  useEffect(() => {
    const hasHours = !!hours && hours > 0;
    const hasDays = !!days && days > 0;

    if (isHoursEnabled) {
      if (hasHours && hasDays) {
        setValue('days', 0);
      } else if (!hasHours) {
        setValue('days', 1);
      }
    } else {
      if (hasHours) {
        setValue('hours', 0);
      }
    }
  }, [isHoursEnabled, startDate, endDate, days, hours, setValue]);

  const hoursInputProps = {
    max: 24,
    min: 0,
    step: '0.25',
  };

  return (
    <>
      <AbsenceDateRange
        employeeId={employeeId}
        formMethods={formMethods}
        publicHolidays={publicHolidays}
        onPickerMonthChange={onPickerMonthChange}
        overlappingAbsence={overlappingAbsence}
      />
      <Controller
        control={control}
        name={'days'}
        render={({ field: { value, ...field }, fieldState: { error } }) => {
          return (
            <TextField
              {...field}
              value={value}
              type="number"
              label={t('days')}
              required
              fullWidth={true}
              margin="normal"
              disabled={!canEditDays}
              InputLabelProps={{
                shrink: (value as number).toString().length > 0,
              }}
              InputProps={{
                inputProps: {
                  max: maxDays,
                  min: 0,
                  step: '1',
                },
                endAdornment: canEditDays ? (
                  <InputAdornment position="end" title={t('laRecalculateDays')}>
                    <IconButton
                      onClick={() => {
                        onResetDaysField();
                      }}
                      disabled={!daysFieldEdited}
                    >
                      {daysFieldEdited ? <SyncDisabled /> : <Sync />}
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              error={!!error}
              helperText={error?.message}
            />
          );
        }}
      />

      {isHoursEnabled && (
        <Box sx={sxStyles.twoFieldRow}>
          <Controller
            control={control}
            name={'hours'}
            render={({ field: { value, ...field }, fieldState: { error } }) => {
              return (
                <TextField
                  {...field}
                  value={value}
                  type="number"
                  label={t('laHours')}
                  required
                  fullWidth={true}
                  margin="normal"
                  InputLabelProps={{
                    shrink: (value as number).toString().length > 0,
                  }}
                  InputProps={{
                    inputProps: hoursInputProps,
                  }}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'plannedWorkingHours'}
            render={({ field: { value, ...field }, fieldState: { error } }) => {
              return (
                <TextField
                  {...field}
                  value={value}
                  type="number"
                  label={t('laPlannedWorkingHours')}
                  required
                  fullWidth={true}
                  margin="normal"
                  InputLabelProps={{
                    shrink: (value as number).toString().length > 0,
                  }}
                  InputProps={{
                    inputProps: hoursInputProps,
                  }}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Box>
      )}
    </>
  );
};
